import { createApi } from '@reduxjs/toolkit/query/react';
import { createBaseQueryWithReauth } from '@/services';
import { PaginatedRequest, PaginatedResponse } from '@/types';
import {
  LogisticOperator,
  LogisticOperatorApi,
  NewLogisticOperator,
  UpdateLogisticOperator,
} from '../models';
import { LogisticOperatorStatus } from '../enums';
import { mapLogisticOperatorApiToModel } from '../adapters';
import { API_BUSINESS_URL } from '@/utils';

const logisticOperatorsService = createApi({
  reducerPath: 'logisticOperatorsService',
  baseQuery: createBaseQueryWithReauth(`${API_BUSINESS_URL}/logistic-operator`),
  tagTypes: ['LogisticOperators'],
  endpoints: (builder) => ({
    getLogisticOperators: builder.query<
      PaginatedResponse<LogisticOperator>,
      {
        paginated: PaginatedRequest;
        search?: string;
        status?: LogisticOperatorStatus;
        businessName?: string;
        parentId?: string;
        headquarter?: boolean;
      }
    >({
      query: ({ paginated, search, status, businessName, parentId, headquarter }) => ({
        url: '',
        params: {
          limit: paginated.pageSize,
          offset: (paginated.page - 1) * paginated.pageSize,
          ...(search && search.trim() !== '' && { name: search.trim() }),
          ...(businessName &&
            businessName.trim() !== '' && {
              businessName: businessName.trim(),
            }),
          ...(status && { status }),
          ...(parentId && { parentId }),
          ...(headquarter !== undefined && { headquarter }),
        },
      }),
      transformResponse: (
        response: PaginatedResponse<LogisticOperatorApi>
      ) => ({
        ...response,
        items: response.items.map(mapLogisticOperatorApiToModel),
      }),
      providesTags: ['LogisticOperators'],
    }),
  
    getUnscopedLogisticOperators: builder.query<
      PaginatedResponse<LogisticOperator>,
      { paginated: PaginatedRequest }
    >({
      query: ({ paginated }) => ({
        url: '/unscoped',
        params: {
          limit: paginated.pageSize,
          offset: (paginated.page - 1) * paginated.pageSize,
          status: LogisticOperatorStatus.ACTIVE,
        },
      }),
      transformResponse: (
        response: PaginatedResponse<LogisticOperatorApi>
      ) => ({
        ...response,
        items: response.items.map(mapLogisticOperatorApiToModel),
      }),
      providesTags: ['LogisticOperators'],
    }),
    getLogisticOperatorById: builder.query<
      LogisticOperator,
      { logisticOperatorId: string }
    >({
      query: ({ logisticOperatorId }) => ({
        url: `/${logisticOperatorId}`,
        params: {
          include:
            'address,address.stateOrDepartment,address.city,address.neighborhood',
        },
      }),
      transformResponse: (response: LogisticOperatorApi) =>
        mapLogisticOperatorApiToModel(response),
      providesTags: ['LogisticOperators'],
    }),
    getHeadquarterLogisticOperators: builder.query<
      PaginatedResponse<LogisticOperator>,
      { paginated: PaginatedRequest }
    >({
      query: ({ paginated }) => ({
        url: '',
        params: {
          limit: paginated.pageSize,
          offset: (paginated.page - 1) * paginated.pageSize,
          headquarter: true,
        },
      }),
      transformResponse: (
        response: PaginatedResponse<LogisticOperatorApi>
      ) => ({
        ...response,
        items: response.items.map(mapLogisticOperatorApiToModel),
      }),
      providesTags: ['LogisticOperators'],
    }),
    createLogisticOperator: builder.mutation<
      LogisticOperator,
      NewLogisticOperator
    >({
      query: (body) => ({
        url: '',
        method: 'POST',
        body,
      }),
      transformResponse: (response: LogisticOperatorApi) =>
        mapLogisticOperatorApiToModel(response),
      invalidatesTags: ['LogisticOperators'],
    }),
    updateLogisticOperator: builder.mutation<
      LogisticOperator,
      { logisticOperatorId: string; body: Omit<UpdateLogisticOperator, 'id'> }
    >({
      query: ({ logisticOperatorId, body }) => ({
        url: `/${logisticOperatorId}`,
        method: 'PUT',
        body,
      }),
      transformResponse: (response: LogisticOperatorApi) =>
        mapLogisticOperatorApiToModel(response),
      invalidatesTags: (_result, _error, { logisticOperatorId }) => [
        'LogisticOperators',
        { type: 'LogisticOperators', id: logisticOperatorId },
      ],
    }),
  }),
});

export default logisticOperatorsService;
export const {
  useGetLogisticOperatorsQuery,
  useGetUnscopedLogisticOperatorsQuery,
  useGetLogisticOperatorByIdQuery,
  useGetHeadquarterLogisticOperatorsQuery,
  useCreateLogisticOperatorMutation,
  useUpdateLogisticOperatorMutation,
} = logisticOperatorsService;
